import { FormControl, Grid, Paper } from '@mui/material';
import React from 'react';
import {
  CalculatorType,
  ALL_DEPARTMENTS_OPTION,
} from 'spw-redux/modules/calculators/types';
import { IProject } from 'spw-redux/modules/projects/types';
import { MenuItem } from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import { ScenarioHeader } from 'spw-components/downloads/image-header';
import Headcount from './headcount';
import DeskSharingGuide from './desk-sharing-guide';
import HeadcountUtilizationGuide from './headcount-utilization-guide';
import GrowthTable from './growth-table';
import {
  DepartmentSelect,
  Container,
  CustomSelect,
  Content,
  ControlsContainer,
  HeadcountSeatcountControlsRow,
  SeatCountFactorContainer,
  FormTitle,
} from './styles';
import { PlainProgramScenario } from '../../models/Scenario';
import VacancyFactorGuide from './vacancy-factor-guide';

interface IProps {
  scenario: PlainProgramScenario;
  isEditingSavedScenario: boolean;
  calculatorType: CalculatorType;
  project: IProject;
  showDecimals: boolean;
  calculatorActions?: JSX.Element;
  isRenderingForExport?: boolean;
}

export const TotalProgram = ({
  scenario,
  isEditingSavedScenario = false,
  calculatorType,
  project,
  showDecimals,
  calculatorActions,
  isRenderingForExport,
}: IProps) => {
  if (!scenario) return null;

  if (!scenario.targetYear) return null;

  return (
    <Content>
      <Paper elevation={0}>
        {(window.SPWExport || isRenderingForExport) && (
          <ScenarioHeader scenario={scenario} styles={{ marginBottom: 15 }} />
        )}
        <Container>
          <ControlsContainer>
            <HeadcountSeatcountControlsRow>
              <FormControl variant="outlined">
                <DepartmentSelect
                  label="Department"
                  value={ALL_DEPARTMENTS_OPTION}
                  disabled
                >
                  <MenuItem
                    key={ALL_DEPARTMENTS_OPTION}
                    value={ALL_DEPARTMENTS_OPTION}
                  >
                    Multiple Departments
                  </MenuItem>
                </DepartmentSelect>
              </FormControl>

              <FormControl variant="outlined">
                <CustomSelect
                  label="Target Year"
                  value={1}
                  placeholder={scenario.compoundLabel}
                  disabled
                >
                  <MenuItem key={1} value={1}>
                    {scenario.compoundLabel}
                  </MenuItem>
                </CustomSelect>
              </FormControl>
            </HeadcountSeatcountControlsRow>
            {calculatorActions}
          </ControlsContainer>
          <FormTitle style={{ marginBottom: 0 }}>
            CALCULATE WORK SEATS
          </FormTitle>
          <SeatCountFactorContainer container style={{ paddingBottom: '20px' }}>
            <Grid
              item
              xs={4}
              style={{
                borderRight: `1px solid ${colors.grayDivider}`,
                paddingRight: '20px',
              }}
              data-testid="headcount_calcs_left_box"
            >
              <Headcount
                title="VACANCY FACTOR"
                base={scenario.headcount}
                factorName="vacancyFactor"
                tooltip={<VacancyFactorGuide />}
                tooltipHeight={255}
                tooltipWidth={298}
                factorValue={scenario.vacancyFactorPercentage?.toString()}
                resultValue={scenario?.headcountWithVacancy}
                disabled
                operator="x"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_calcs_left_box"
              />
            </Grid>
            <Grid
              item
              xs={4}
              style={{
                borderRight: `1px solid ${colors.grayDivider}`,
                padding: '0 20px',
              }}
              data-testid="headcount_calcs_middle_box"
            >
              <Headcount
                title="HEADCOUNT UTILIZATION"
                factorName="headcountUtilization"
                base={scenario?.headcountWithVacancy}
                tooltip={<HeadcountUtilizationGuide />}
                tooltipHeight={323}
                tooltipWidth={298}
                factorValue={Math.round(
                  scenario.headcountUtilizationPercentage
                ).toString()}
                resultValue={scenario?.headcountWithUtilization}
                disabled
                operator="x"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_calcs_middle_box"
              />
            </Grid>
            <Grid
              item
              xs={4}
              style={{ paddingLeft: '20px' }}
              data-testid="headcount_calcs_right_box"
            >
              <Headcount
                title="DESK SHARING RATIO"
                factorName="deskSharingRatio"
                disabled
                base={scenario?.headcountWithUtilization}
                rightLabel="Work Seats"
                tooltip={<DeskSharingGuide />}
                tooltipHeight={559}
                tooltipWidth={298}
                showRatio
                factorValue={scenario?.deskSharingRatio?.toString()}
                resultValue={scenario?.workSeats}
                operator="÷"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_calcs_right_box"
              />
            </Grid>
          </SeatCountFactorContainer>
        </Container>
        {scenario.growths[0]?.growthEntries?.length > 0 && (
          <GrowthTable
            isRenderingForExport={isRenderingForExport}
            project={project}
            scenario={scenario}
            calculatorType={calculatorType}
            isEditingSavedScenario={isEditingSavedScenario}
            testingIdPrefix="growth_calcs"
          />
        )}
      </Paper>
    </Content>
  );
};

export default TotalProgram;
